import axios from 'axios'
import { isEmpty } from 'lodash'
import { MASTER_DATA_FIELDS } from '~/common/constants'
import { action, computed, observable, persist, store } from '~/common/mobx.decorator'
import { apolloClient, LIKE_DISLIKE_JOB_MUTATION, SEARCH_RECRUITERS_FOR_FILTER_QUERY, SEARCH_WORKER_OPTIONS_QUERY } from '~/common/apollo'
import { notifyStore } from '~/stores'

@store()
class MasterStore {
	@observable disciplines
	@observable locations
	@observable referrals
	@observable agencies
	@observable chartings
	@observable primarySpecialities
	@observable secondarySpecialities
	@persist @observable licenseStates = []
	@observable licenseStatesAll = [] // Store all license states for client-side filtering
	@observable referenceRelationships
	@observable jobDisciplines
	@observable jobShifts
	@observable profession
	@observable originalSpecialties = {}
	@observable specialties = []
	@observable reasons = []
	@observable locations = []
	@observable salutations = []
	@observable relationships = []
	@observable documentTypes = []
	@observable facilityTypes = []
	@observable jobSettings = []
	@observable referredRelationships = []
	@observable workLocations = []
	@observable originalLocations = []
	@observable assignmentTypes = []
	@observable cityStates = []
	@observable jobApplicantStage = []
	@observable workerAssignmentStage = []
	@observable activityTypes = []
	@observable recruiterRoles = []
	@observable recruiterFullRoles = []
	@observable states = []
	@observable statesAll = [] // Full unfiltered state list; `states` is the narrowed view of it
	@observable cities = []
	@observable retentionStatues = []
	@observable atsStatuses = []
	@observable atsSources = []
	@observable atsSourcesAll = []
	@observable employerNames = []
	// Keyword the stored list was fetched with ('' = the unfiltered first page).
	@observable _employerNamesKeyword = null
	@observable _citiesKeyword = null
	// Candidate-name options for the Matches "Candidate" column filter autocomplete.
	@observable matchCandidateOptions = []
	@persist @observable specialtyOptions = []
	@persist @observable listSpecialtyName = []
	@persist @observable jobTypes = []
	@persist @observable maestroJobEmploymentTypes = []
	@observable maestroJobEmploymentTypesAll = [] // Store all job employment types for client-side filtering
	@persist @observable maestroWorkerEmploymentTypes = []
	@observable maestroWorkerEmploymentTypesAll = [] // Store all worker employment types for client-side filtering
	@persist @observable maestroJobApplicantEmploymentTypes = []
	@persist @observable maestroWorkerAssignmentEmploymentTypes = []
	@observable maestroWorkerAssignmentEmploymentTypesAll = [] // Store all employment types for client-side filtering
	@persist @observable masterData = {}
	@persist @observable frequencyMasterData = {}
	@persist @observable alertChannelMasterData = {}
	@persist @observable pauseAlertPeriodMasterData = {}

	/*
	 * `/states` is unpaginated and the set is fixed, so fetch it once and narrow it
	 * in memory — the same shape fetchAtsSources already uses. Previously every
	 * keystroke in the state filter, and every remount of a tab that opens one,
	 * went to the network for a list that had not changed.
	 */
	@action
	fetchStates = async (force = false, name) => {
		if (force || isEmpty(this.statesAll)) {
			const response = await axios.get('/states')
			this.statesAll =
				response.data?.map((item) => {
					const stateCode = item.code || item.state_code
					const stateName = item.name
					// Display state name with abbreviation in parentheses, e.g., "Arizona (AZ)"
					const label = stateCode ? `${stateName} (${stateCode})` : stateName

					return {
						value: stateName, // Full state name (e.g., "New York") - for filtering and display
						label: label, // State name with code for display in dropdown
						code: stateCode, // State code (e.g., "NY") - if available
					}
				}) || []
		}

		if (name && name.trim() !== '') {
			const searchTerm = name.trim().toLowerCase()
			this.states = this.statesAll.filter((item) => item.label?.toLowerCase().includes(searchTerm))
		} else {
			this.states = [...this.statesAll]
		}
	}

	/*
	 * Unlike states, `/cities` is server-filtered and capped at 50 rows, so the
	 * unfiltered response is a first page rather than the whole set and cannot be
	 * narrowed in memory. Only the repeat of that unfiltered first page is skipped,
	 * and only while the stored list still *is* that page — `_citiesKeyword` is what
	 * makes clearing the search box refetch instead of keeping the narrowed list.
	 */
	@action
	fetchCities = async (force = false, name) => {
		const keyword = name && name.trim() !== '' ? name.trim() : ''

		if (!force && !keyword && this._citiesKeyword === '' && !isEmpty(this.cities)) {
			return
		}
		this._citiesKeyword = keyword

		const params = { limit: 50, offset: 0, city_only: true }
		if (keyword) {
			params.name = keyword
		}

		const response = await axios.get('/cities', { params })
		const citiesData = response.data || []

		// Always show state code for consistency, as many cities have the same name across different states
		this.cities =
			citiesData.map((item) => {
				const label = `${item.name}, ${item.state_code}`
				const value = `${item.name}, ${item.state_code}`

				return {
					value,
					label,
					stateCode: item.state_code,
					stateName: item.state_name,
				}
			}) || []
	}

	/**
	 * `full_roles` minus the client-portal roles.
	 *
	 * The picklist is `Role::FIXED_EMPLOYER_SCOPES` on the backend, which is the
	 * four internal `maestro_*` scopes plus `recruiter_admin` and
	 * `recruiter_manager` — client-portal roles, not aliases of the Maestro ones.
	 * No row on the User Roles screen can hold them: the recruiter search applies
	 * `roles: MAESTRO_EMPLOYER_SCOPES` as a default filter, so the list is
	 * internal staff only. Offered anyway they were a role filter that matched
	 * nothing, a reference card for a role nobody on the screen holds, and a
	 * settable saved-view default for a role that never appears.
	 *
	 * Same rule the backend applies to build that list:
	 * `FIXED_EMPLOYER_SCOPES.grep(/\Amaestro_/)`.
	 *
	 * `recruiterFullRoles` keeps the unfiltered response, so a caller that really
	 * does want every tenant role still has it.
	 */
	@computed
	get maestroRoles() {
		return (this.recruiterFullRoles || []).filter((role) => String(role?.value || '').startsWith('maestro_'))
	}

	@action
	fetchRecruiterFullRoles = async (force = false) => {
		if (!force && !isEmpty(this.recruiterFullRoles)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.recruiterFullRoles,
			},
		})
		this.recruiterFullRoles = response.data?.map((item) => ({ value: item.id, label: item.name }))
	}

	@action
	fetchRecruiterRoles = async (force = false) => {
		if (!force && !isEmpty(this.recruiterRoles)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.recruiterRoles,
			},
		})
		this.recruiterRoles = response.data?.map((item) => ({ value: item.id, label: item.name }))
	}

	@action
	fetchJobStage = async (force = false) => {
		if (!force && !isEmpty(this.jobApplicantStage)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.jobApplicantStage,
			},
		})
		this.jobApplicantStage = response.data
	}

	@action
	fetchRetentionStatues = async (force = false) => {
		if (!force && !isEmpty(this.retentionStatues)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.retentionStatues,
			},
		})
		this.retentionStatues = response.data?.map((item) => ({ value: item.id, label: item.name }))
	}

	@action
	fetchAtsStatuses = async (force = false) => {
		if (!force && !isEmpty(this.atsStatuses)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.atsStatuses,
			},
		})
		this.atsStatuses = response.data?.map((item) => ({ value: item.id, label: item.name }))
	}

	@action
	fetchAtsSources = async (force = false, name) => {
		if (force || isEmpty(this.atsSourcesAll)) {
			const response = await axios.get('/picklists', {
				params: {
					picklist_type: MASTER_DATA_FIELDS.atsSources,
				},
			})
			this.atsSourcesAll =
				response.data?.map((item) => ({
					label: item.name,
					value: item.id,
				})) || []
		}

		if (name && name.trim() !== '') {
			const searchTerm = name.trim().toLowerCase()
			this.atsSources = this.atsSourcesAll.filter((item) => {
				return item.label?.toLowerCase().includes(searchTerm) || item.value?.toLowerCase().includes(searchTerm)
			})
		} else {
			this.atsSources = [...this.atsSourcesAll]
		}
	}

	@action
	fetchEmployerNames = async (force = false, name) => {
		const keyword = name && name.trim() !== '' ? name.trim() : ''

		/*
		 * `force` was declared here but never read, so this had no guard at all —
		 * and ten tab components call it from useEffectOnce, so simply moving
		 * between tabs re-issued the same unfiltered query every time. Guarded the
		 * same way as fetchCities: the result is a server-filtered page of 50, so
		 * only the unfiltered call is cacheable, and only while it is what is stored.
		 */
		if (!force && !keyword && this._employerNamesKeyword === '' && !isEmpty(this.employerNames)) {
			return
		}
		this._employerNamesKeyword = keyword

		const filter = keyword ? { keyword } : {}

		const response = await apolloClient().query({
			query: SEARCH_RECRUITERS_FOR_FILTER_QUERY,
			variables: {
				first: 50,
				filter,
				sortBy: { field: 'fullName', order: 'asc' },
			},
		})

		this.employerNames =
			response?.data?.recruiters?.nodes?.map((item) => {
				// Join only the parts that exist so a missing first/last name doesn't
				// render the literal string "null" (e.g. "Accounting null").
				const fullName = [item.firstName, item.lastName].filter(Boolean).join(' ').trim()
				let label
				if (fullName && item.email) {
					label = `${fullName} (${item.email})`
				} else {
					label = fullName || item.email || ''
				}
				return {
					label,
					value: item.id,
				}
			}) || []
	}

	// Candidate-name autocomplete for the Matches "Candidate" column filter.
	// Scoped to `matches` so suggestions are limited to candidates that can
	// actually appear in the table. `keyword` matches name + email on the BE.
	@action
	fetchMatchCandidateNames = async (force = false, keyword) => {
		const filter = { workerScope: 'matches' }
		if (keyword && keyword.trim() !== '') {
			filter.keyword = keyword.trim()
		}

		const response = await apolloClient().query({
			query: SEARCH_WORKER_OPTIONS_QUERY,
			variables: {
				first: 50,
				filter,
				sortBy: { field: 'fullName', order: 'asc' },
			},
		})

		this.matchCandidateOptions =
			response?.data?.searchWorkers?.nodes?.map((item) => {
				const fullName = (item.fullName || '').trim()
				let label
				if (fullName && item.email) {
					label = `${fullName} (${item.email})`
				} else {
					label = fullName || item.email || ''
				}
				return { label, value: item.id }
			}) || []
	}

	@action
	fetchWorkerAssignmentStage = async (force = false) => {
		if (!force && !isEmpty(this.workerAssignmentStage)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.workerAssignmentStage,
			},
		})
		this.workerAssignmentStage = response.data
	}

	@action
	fetchTypeJobs = async (force = false) => {
		// Use maestroWorkerEmploymentTypes for jobTypes
		await this.fetchMaestroWorkerEmploymentTypes(force)
		this.jobTypes = this.maestroWorkerEmploymentTypes
	}

	@action
	fetchMaestroJobEmploymentTypes = async (force = false, name) => {
		// Fetch all job employment types if we don't have them yet or if force is true
		if (force || isEmpty(this.maestroJobEmploymentTypesAll)) {
			const response = await axios.get('/picklists', {
				params: {
					picklist_type: MASTER_DATA_FIELDS.maestroJobEmploymentTypes,
				},
			})
			this.maestroJobEmploymentTypesAll =
				response.data?.map((item) => ({
					label: item.name,
					value: item.name,
				})) || []
		}

		// Filter client-side if name is provided
		if (name && name.trim() !== '') {
			const searchTerm = name.trim().toLowerCase()
			this.maestroJobEmploymentTypes = this.maestroJobEmploymentTypesAll.filter((item) => {
				return item.label?.toLowerCase().includes(searchTerm) || item.value?.toLowerCase().includes(searchTerm)
			})
		} else {
			// No search term, show all job employment types
			this.maestroJobEmploymentTypes = [...this.maestroJobEmploymentTypesAll]
		}
	}

	@action
	fetchMaestroWorkerEmploymentTypes = async (force = false, name) => {
		// Fetch all worker employment types if we don't have them yet or if force is true
		if (force || isEmpty(this.maestroWorkerEmploymentTypesAll)) {
			const response = await axios.get('/picklists', {
				params: {
					picklist_type: MASTER_DATA_FIELDS.maestroWorkerEmploymentTypes,
				},
			})
			this.maestroWorkerEmploymentTypesAll =
				response.data?.map((item) => ({
					label: item.name,
					value: item.name,
				})) || []
		}

		// Filter client-side if name is provided
		if (name && name.trim() !== '') {
			const searchTerm = name.trim().toLowerCase()
			this.maestroWorkerEmploymentTypes = this.maestroWorkerEmploymentTypesAll.filter((item) => {
				return item.label?.toLowerCase().includes(searchTerm) || item.value?.toLowerCase().includes(searchTerm)
			})
		} else {
			// No search term, show all worker employment types
			this.maestroWorkerEmploymentTypes = [...this.maestroWorkerEmploymentTypesAll]
		}
	}

	@action
	fetchMaestroJobApplicantEmploymentTypes = async (force = false) => {
		if (!force && !isEmpty(this.maestroJobApplicantEmploymentTypes)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.maestroJobApplicantEmploymentTypes,
			},
		})
		this.maestroJobApplicantEmploymentTypes = response.data?.map((item) => ({
			label: item.name,
			value: item.name,
		}))
	}

	@action
	fetchMaestroWorkerAssignmentEmploymentTypes = async (force = false, name) => {
		// Fetch all employment types if we don't have them yet or if force is true
		if (force || isEmpty(this.maestroWorkerAssignmentEmploymentTypesAll)) {
			const response = await axios.get('/picklists', {
				params: {
					picklist_type: MASTER_DATA_FIELDS.maestroWorkerAssignmentEmploymentTypes,
				},
			})
			this.maestroWorkerAssignmentEmploymentTypesAll =
				response.data?.map((item) => ({
					label: item.name,
					value: item.name,
				})) || []
		}

		// Filter client-side if name is provided
		if (name && name.trim() !== '') {
			const searchTerm = name.trim().toLowerCase()
			this.maestroWorkerAssignmentEmploymentTypes = this.maestroWorkerAssignmentEmploymentTypesAll.filter((item) => {
				return item.label?.toLowerCase().includes(searchTerm) || item.value?.toLowerCase().includes(searchTerm)
			})
		} else {
			// No search term, show all employment types
			this.maestroWorkerAssignmentEmploymentTypes = [...this.maestroWorkerAssignmentEmploymentTypesAll]
		}
	}

	@action
	fetchJobShifts = async (force = false) => {
		if (!force && !isEmpty(this.jobShifts)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: 'shift',
			},
		})
		this.jobShifts = response.data?.map((item) => ({ label: item.name, value: item.id }))
	}

	@action
	fetchLocations = async (force = false) => {
		if (!force && !isEmpty(this.locations)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.preferredLocations,
			},
		})

		this.locations = response.data
	}

	@action
	fetchReferenceRelationships = async (force = false) => {
		if (!force && !isEmpty(this.referenceRelationships)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.referenceRelationships,
			},
		})

		this.referenceRelationships = response?.data || []
	}

	@action
	fetchTravelerRelationship = async (force = false) => {
		if (!force && !isEmpty(this.referredRelationships)) {
			return
		}
		const response = await axios.get('/company_configs/master_data')

		const relationships = response?.data?.referred_traveler?.relationship
		let data = Object.keys(relationships).map((key) => ({
			label: relationships[key],
			value: key,
		}))

		this.referredRelationships = data
	}

	@action
	fetchReferrals = async (force = false) => {
		if (!force && !isEmpty(this.referrals)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.referral,
			},
		})

		this.referrals = response.data
	}

	@action
	fetchAgencies = async (force = false) => {
		if (!force && !isEmpty(this.agencies)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.agency,
			},
		})

		this.agencies = response.data
	}

	@action
	fetchChartings = async (force = false) => {
		if (!force && !isEmpty(this.chartings)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.charting,
			},
		})

		this.chartings = response.data
	}

	@action
	fetchSecondarySpecialities = async (force = false) => {
		if (!force && !isEmpty(this.secondarySpecialities)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.secondarySpecialty,
			},
		})

		this.secondarySpecialities = response.data
	}

	@action
	fetchLicenseStates = async (force = false, name) => {
		// Fetch all license states if we don't have them yet or if force is true
		if (force || isEmpty(this.licenseStatesAll)) {
			const params = {
				picklist_type: MASTER_DATA_FIELDS.licenseState,
			}

			const response = await axios.get('/picklists', { params })

			// Handle array response format: [{ id, name, state_code, ... }]
			if (Array.isArray(response.data)) {
				this.licenseStatesAll = response.data.map((item) => ({
					label: item.state_code ? `${item.name} (${item.state_code})` : item.name,
					value: item.state_code || item.id,
					name: item.name, // Store original name for better search
					stateCode: item.state_code, // Store state code for search by abbreviation
				}))
			} else {
				// Fallback for object format (backward compatibility)
				this.licenseStatesAll = Object.entries(response.data).map(([key, value]) => ({
					label: value,
					value: key,
					name: value,
					stateCode: key,
				}))
			}
		}

		// Filter client-side if name is provided
		if (name && name.trim() !== '') {
			const searchTerm = name.trim().toLowerCase()
			this.licenseStates = this.licenseStatesAll.filter((item) => {
				// Search in label (contains both name and code)
				const labelMatch = item.label?.toLowerCase().includes(searchTerm)
				// Search in name directly
				const nameMatch = item.name?.toLowerCase().includes(searchTerm)
				// Search in state code (abbreviation) - case insensitive exact match or contains
				const codeMatch = item.stateCode?.toLowerCase().includes(searchTerm)

				return labelMatch || nameMatch || codeMatch
			})
		} else {
			// No search term, show all license states
			this.licenseStates = [...this.licenseStatesAll]
		}
	}

	@action
	fetchPrimarySpecialities = async (force = false) => {
		if (!force && !isEmpty(this.primarySpecialities)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.specialty,
			},
		})

		this.primarySpecialities = response.data?.map((item) => ({ value: item.id, label: item.name }))
	}

	@action
	onFavoriteJob = async (values) => {
		const { jobId, action } = values?.variables
		try {
			const response = await apolloClient().mutate({
				mutation: LIKE_DISLIKE_JOB_MUTATION,
				variables: {
					jobId: jobId,
					action: action,
				},
			})
			notifyStore.success('$MESSAGES.JOB_PREFERENCE_SAVED')
			return response?.data?.likeOrDislikeAJob
		} catch (error) {
			notifyStore.error(error)
		}
	}

	@action
	fetchRelationships = async (force = false) => {
		if (!force && !isEmpty(this.relationships)) {
			return
		}

		const response = await axios.get('/company_configs/master_data')
		this.relationships = response?.data?.worker_reference?.relationship
	}

	@action
	fetchSalutations = async (force = false) => {
		if (!force && !isEmpty(this.salutations)) {
			return
		}

		const response = await axios.get('/company_configs/master_data')
		this.salutations = response?.data?.worker_reference?.salutation
	}

	@action
	fetchReasonLeavings = async (force = false) => {
		if (!force && !isEmpty(this.reasons)) {
			return
		}
		const response = await axios.get('/company_configs/master_data')
		this.reasons = response?.data?.work_experience?.reason_for_leaving
	}

	@action
	fetchAssignmentTypes = async (force = false) => {
		if (!force && !isEmpty(this.assignmentTypes)) {
			return
		}
		const response = await axios.get('/company_configs/master_data')
		this.assignmentTypes = response?.data?.work_experience?.assignment_type
	}

	@action
	fetchSupportDocumentTypes = async (force = false) => {
		if (!force && !isEmpty(this.documentTypes)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.documentType,
			},
		})

		this.documentTypes = response.data
	}

	@action
	fetchSpecialties = async (force = false) => {
		if (!force && !isEmpty(this.specialtyOptions)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.discipline,
			},
		})

		const disciplines = response?.data?.map((item) => ({
			label: item?.name || '',
			value: item?.id || '',
			specialties: item?.specialties?.map((item) => ({ label: item?.name || '', value: item?.id || '' })),
			groupLabel: 'Discipline',
			// Allow discipline items to be selectable
		}))

		const specialties = response?.data?.reduce((accumulative, currentItem) => {
			const dependentValues = currentItem?.specialties?.map((item) => ({
				label: item?.name || '',
				value: item?.id || '',
				disciplineId: currentItem?.id || '',
				groupLabel: currentItem?.name || '',
			}))
			return [...accumulative, ...dependentValues]
		}, [])

		this.specialtyOptions = [...specialties, ...disciplines]
		this.disciplines = disciplines

		const removeDuplicates = (options) => {
			const seen = new Map()
			return options.filter((option) => !seen.has(option.value) && seen.set(option.value, 1))
		}

		this.primarySpecialities = [...removeDuplicates(specialties)]
	}

	@action
	fetchFacilityTypes = async (_force = false, name) => {
		const response = await axios.get(`/facilities${name ? `?name=${name}` : ''}`)

		const facilities = response.data?.map((item) => ({
			label: item?.name,
			value: item?.id,
			groupLabel: 'Facility',
		}))

		const facilityTypes = response.data?.reduce((accumulative, currentItem) => {
			const dependentValues =
				currentItem?.facility_types?.map((item) => ({
					label: item,
					value: item,
					groupLabel: currentItem.name,
				})) || []
			return [...accumulative, ...dependentValues]
		}, [])

		this.facilityTypes = [...facilityTypes, ...facilities]
	}

	@action
	fetchJobSettings = async (force = false) => {
		if (!force && !isEmpty(this.jobSettings)) {
			return
		}
		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.jobSettings,
			},
		})
		this.jobSettings = response.data.map((item) => ({
			label: item?.name,
			value: item?.id,
		}))
	}

	@action
	fetchActivityTypes = async (force = false) => {
		if (!force && !isEmpty(this.activityTypes)) {
			return
		}
		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.activityTypes,
			},
		})
		this.activityTypes = response.data
			.map((item) => ({
				label: item?.name,
				value: item?.id,
			}))
			?.filter((item) => item?.value !== 'score_activities')
	}

	@action
	fetchWorkLocations = async (force = false) => {
		if (!force && !isEmpty(this.workLocations)) {
			return
		}

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.state,
			},
		})

		const locations = response?.data

		this.originalLocations = response?.data
		let data = Object.keys(locations).map((key) => ({
			label: locations[key],
			value: key,
		}))

		this.workLocations = data
	}

	@action
	fetchCityState = async (force = false) => {
		if (!force && !isEmpty(this.cityStates)) {
			return
		}

		// const response = await axios.get(`/cities/search?terms=${input}`)

		const response = await axios.get('/picklists', {
			params: {
				picklist_type: MASTER_DATA_FIELDS.cities,
			},
		})

		const locations = response?.data

		this.originalLocations = response?.data
		let data = Object.keys(locations).map((key) => ({
			label: locations[key],
			value: key,
		}))

		this.cityStates = data
	}
}

export const masterStore = new MasterStore()
